import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Button, IconButton, Typography,
  Drawer, List, ListItem, ListItemButton, ListItemText,
  useMediaQuery, useTheme,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import MenuIcon from '@mui/icons-material/Menu';
import { useI18n } from '../../utils/i18n';

export default function LandingNavbar() {
  const { t, toggleLang } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnchor = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const drawerContent = (
    <Box sx={{ width: 250, pt: 2 }}>
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleAnchor('features')}>
            <ListItemText primary={t('landing.nav.features')} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleAnchor('testimonials')}>
            <ListItemText primary={t('landing.nav.testimonials')} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={toggleLang}>
            <ListItemText primary="Language" />
            <TranslateIcon fontSize="small" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigate('/login')}>
            <ListItemText primary={t('landing.nav.login')} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigate('/register')}>
            <ListItemText primary={t('landing.nav.signup')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={scrolled ? 1 : 0}
      sx={{
        bgcolor: scrolled ? 'background.paper' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          color="primary"
          fontWeight={700}
          sx={{ cursor: 'pointer' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Sorane
        </Typography>

        {isMobile ? (
          <>
            <IconButton
              edge="end"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              PaperProps={{ sx: { bgcolor: 'background.paper' } }}
            >
              {drawerContent}
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button color="inherit" onClick={() => handleAnchor('features')}>
              {t('landing.nav.features')}
            </Button>
            <Button color="inherit" onClick={() => handleAnchor('testimonials')}>
              {t('landing.nav.testimonials')}
            </Button>
            <IconButton onClick={toggleLang} sx={{ color: 'text.secondary' }}>
              <TranslateIcon fontSize="small" />
            </IconButton>
            <Button color="inherit" onClick={() => navigate('/login')}>
              {t('landing.nav.login')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/register')}
            >
              {t('landing.nav.signup')}
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
